import React, { useEffect } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  ShippingCartItem,
  ShippingCartItemViewProps,
  ShippingCartType,
} from "./ShippingCart.types";
import {
  getMuiLocalizationProviderAdapterName,
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en"; // US
import "dayjs/locale/en-gb"; // UK
import "dayjs/locale/en-ca"; // CA

import {
  DimensionUnit,
  ValidationErrorEntity,
  WeightUnit,
  addFieldError,
  getFieldError,
  removeFieldError,
} from "packable-common";

import "./ShippingCart.css";

const useStyles = (theme: Theme) => ({
  ShippingCartItemViewContainer: {
    width: "100%",
    pb: 2,
  },
  ShippingCartItemViewContainerDivider: {
    mb: 2,
  },
  ShippingCartItemViewRowContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "center",
  },
  ShippingCartItemViewSingleItemRowContainer: {
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  ShippingCartItemViewContentShortTextFieldContainer: {
    padding: 1,
  },
  ShippingCartItemViewContentShortTextErrorFieldContainer: {
    display: "flex",
    flexDirection: "column",
  },
  ShippingCartItemViewContentShortTextErrorText: {
    color: theme.palette.error.dark,
    muiVariant: "caption",
  },
  ShippingCartItemViewContentShortTextField: {
    width: "240px",
  },
  ShippingCartItemViewContentFullWidthTextFieldContainer: {
    width: "100%",
    padding: 1,
  },
  ShippingCartItemViewContentFullWidthTextField: {
    width: "100%",
  },
  ShippingCartItemViewContentSectionTitleContainer: {
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
    padding: 1,
  },
  ShippingCartItemViewContentSectionTitle: {
    muiVariant: "h6",
  },
  ShippingCartItemViewContentSectionTitleDivider: {
    width: "100%",
    marginBottom: 2,
  },
  ShippingCartItemViewContentActionRow: {
    padding: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  ShippingCartItemViewContentActionRemoveButton: {
    muiVariant: "outlined",
    muiColor: "secondary",
  },
});

export const ShippingCartItemView: React.FC<ShippingCartItemViewProps> = (
  props
): React.JSX.Element => {
  const {
    shippingCartItem,
    shippingCartType,
    multipleItems = false,
    onShippingCartItemUpdate,
    validationErrors,
    onValidationErrorsUpdate,
    onRemoveShippingCartItem,
    userAppState,
    styles,
  } = props;

  logDebug("ShippingCartItemView PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("ShippingCartItemView effectiveStyles");
  logDebug(effectiveStyles);

  const setValidationError = (
    fieldName: string,
    validationError: string
  ): void => {
    onValidationErrorsUpdate(
      addFieldError(
        new ValidationErrorEntity(fieldName, validationError),
        validationErrors
      )
    );
  };

  const removeValidationError = (fieldName: string): void => {
    onValidationErrorsUpdate(removeFieldError(fieldName, validationErrors));
  };

  const validateItemSizeInches = () => {
    let sizeArray: Array<number> = [];
    sizeArray.push(shippingCartItem.itemLength as number);
    sizeArray.push(shippingCartItem.itemWidth as number);
    sizeArray.push(shippingCartItem.itemHeight as number);
    sizeArray.sort((a, b) => b - a);

    const largestDimension: number = Number(sizeArray[0] || 0);
    const girth: number =
      Number((sizeArray[1] || 0) * 2) + (sizeArray[2] || 0) * 2;
    const totalSize: number = largestDimension + girth;
    if (totalSize > 130) {
      setValidationError(
        "itemLabel",
        `We can only ship items with the maximum size of 130 inches in combined length and girth. This item has size of ${totalSize} inches`
      );
    } else {
      removeValidationError("itemLabel");
    }
  };

  const validateItemSizeCentimeters = () => {
    let sizeArray: Array<number> = [];
    sizeArray.push(shippingCartItem.itemLength as number);
    sizeArray.push(shippingCartItem.itemWidth as number);
    sizeArray.push(shippingCartItem.itemHeight as number);
    sizeArray.sort((a, b) => b - a);

    const largestDimension: number = Number(sizeArray[0] || 0);
    const girth: number =
      Number((sizeArray[1] || 0) * 2) + (sizeArray[2] || 0) * 2;
    const totalSize: number = largestDimension + girth;
    if (totalSize > 300) {
      setValidationError(
        "itemLabel",
        `We can only ship items with the maximum size of 300 centimeters in combined length and girth. This item has size of ${totalSize} inches`
      );
    } else {
      removeValidationError("itemLabel");
    }
  };

  const validateEndStorageDate = () => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 30);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 365);
    if (
      shippingCartItem.storageEndDate !== undefined &&
      shippingCartItem.storageEndDate !== null
    ) {
      if (
        shippingCartItem.storageEndDate < minDate ||
        shippingCartItem.storageEndDate > maxDate
      ) {
        setValidationError(
          "storageEndDate",
          "We offer storage periods from 1 month up to 12 months"
        );
      } else {
        removeValidationError("storageEndDate");
      }
    }
  };

  const validateLengthInches = () => {
    if (Number(shippingCartItem.itemLength) === 0) {
      setValidationError("itemLength", "Please enter item length");
    } else if (!/^[0-9]{1,3}$/.test(shippingCartItem.itemLength.toString())) {
      setValidationError("itemLength", "Please enter item length");
    } else if (
      Number(shippingCartItem.itemLength) < 1 ||
      Number(shippingCartItem.itemLength) > 130
    ) {
      setValidationError(
        "itemLength",
        "We can only ship items with the maximium size of 130 inches"
      );
    } else {
      removeValidationError("itemLength");
    }
  };

  const validateLengthCentimeters = () => {
    if (Number(shippingCartItem.itemLength) === 0) {
      setValidationError("itemLength", "Please enter item length");
    } else if (!/^[0-9]{1,3}$/.test(shippingCartItem.itemLength.toString())) {
      setValidationError("itemLength", "Please enter item length");
    } else if (
      Number(shippingCartItem.itemLength) < 10 ||
      Number(shippingCartItem.itemLength) > 150
    ) {
      setValidationError(
        "itemLength",
        "We can only ship items with the maximium size of 150 centimeters"
      );
    } else {
      removeValidationError("itemLength");
    }
  };

  const validateWidthInches = () => {
    if (Number(shippingCartItem.itemWidth) === 0) {
      setValidationError("itemWidth", "Please enter item width");
    } else if (!/^[0-9]{1,3}$/.test(shippingCartItem.itemWidth.toString())) {
      setValidationError("itemWidth", "Please enter item width");
    } else if (
      Number(shippingCartItem.itemWidth) < 1 ||
      Number(shippingCartItem.itemWidth) > 130
    ) {
      setValidationError(
        "itemLength",
        "We can only ship items with the maximium size of 130 inches"
      );
    } else {
      removeValidationError("itemWidth");
    }
  };

  const validateWidthCentimeters = () => {
    if (Number(shippingCartItem.itemWidth) === 0) {
      setValidationError("itemWidth", "Please enter item width");
    } else if (!/^[0-9]{1,3}$/.test(shippingCartItem.itemWidth.toString())) {
      setValidationError("itemWidth", "Please enter item width");
    } else if (
      Number(shippingCartItem.itemWidth) < 10 ||
      Number(shippingCartItem.itemWidth) > 150
    ) {
      setValidationError(
        "itemWidth",
        "We can only ship items with the maximium size of 150 centimeters"
      );
    } else {
      removeValidationError("itemWidth");
    }
  };

  const validateHeightInches = () => {
    if (Number(shippingCartItem.itemHeight) === 0) {
      setValidationError("itemHeight", "Please enter item height");
    } else if (!/^[0-9]{1,3}$/.test(shippingCartItem.itemHeight.toString())) {
      setValidationError("itemHeight", "Please enter item height");
    } else if (
      Number(shippingCartItem.itemHeight) < 1 ||
      Number(shippingCartItem.itemHeight) > 130
    ) {
      setValidationError(
        "itemHeight",
        "We can only ship items with the maximium size of 130 inches"
      );
    } else {
      removeValidationError("itemHeight");
    }
  };

  const validateHeightCentimeters = () => {
    if (Number(shippingCartItem.itemHeight) === 0) {
      setValidationError("itemHeight", "Please enter item height");
    } else if (!/^[0-9]{1,3}$/.test(shippingCartItem.itemHeight.toString())) {
      setValidationError("itemHeight", "Please enter item height");
    } else if (
      Number(shippingCartItem.itemHeight) < 10 ||
      Number(shippingCartItem.itemHeight) > 150
    ) {
      setValidationError(
        "itemHeight",
        "We can only ship items with the maximium size of 150 centimeters"
      );
    } else {
      removeValidationError("itemHeight");
    }
  };

  const validateWeightPounds = () => {
    if (Number(shippingCartItem.itemWeight) === 0) {
      setValidationError("itemWeight", "Please enter item weight");
    } else if (!/^[0-9]{1,2}$/.test(shippingCartItem.itemWeight.toString())) {
      setValidationError("itemWeight", "Please enter item weight");
    } else if (
      Number(shippingCartItem.itemWeight) < 1 ||
      Number(shippingCartItem.itemWeight) > 70
    ) {
      setValidationError(
        "itemWeight",
        "We can only ship items that weigh up to 70 pounds"
      );
    } else {
      removeValidationError("itemWeight");
    }
  };

  const validateWeightKilograms = () => {
    if (Number(shippingCartItem.itemWeight) === 0) {
      setValidationError("itemWeight", "Please enter item weight");
    } else if (!/^[0-9]{1,2}$/.test(shippingCartItem.itemWeight.toString())) {
      setValidationError("itemWeight", "Please enter item weight");
    } else if (
      Number(shippingCartItem.itemWeight) < 1 ||
      Number(shippingCartItem.itemWeight) > 30
    ) {
      setValidationError(
        "itemWeight",
        "We can only ship items that weigh up to 30 kilograms"
      );
    } else {
      removeValidationError("itemWeight");
    }
  };

  const validateShippingCartItem = () => {
    if (userAppState?.units?.weightUnit === WeightUnit.KILOGRAM) {
      validateWeightKilograms();
    } else {
      validateWeightPounds();
    }
    if (userAppState?.units?.dimensionUnit === DimensionUnit.CENTIMETER) {
      validateLengthCentimeters();
      validateWidthCentimeters();
      validateHeightCentimeters();
      validateItemSizeCentimeters();
    } else {
      validateLengthInches();
      validateWidthInches();
      validateHeightInches();
      validateItemSizeInches();
    }
    validateEndStorageDate();
  };

  const handleUseTheSameDateValueChange = (event: any) => {
    let updatedShippingCartItem = packableDeepClone(shippingCartItem);
    updatedShippingCartItem.useTheSameDateAndTime =
      !updatedShippingCartItem.useTheSameDateAndTime;
    onShippingCartItemUpdate(updatedShippingCartItem);
  };

  const onDateChange = (event: any) => {
    if (event) {
      if (event instanceof dayjs) {
        const newValue = (event as Dayjs).toDate();
        let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
        const storageEndDate = new Date(newValue);
        updatedCart.storageEndDate = new Date(storageEndDate);
        onShippingCartItemUpdate(updatedCart);
      }
    }
  };

  const onLengthChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemLength = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onWidthChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemWidth = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onHeightChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemHeight = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onWeightChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemWeight = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onRemoveItem = () => {
    onRemoveShippingCartItem(shippingCartItem.itemId);
  };

  const getDimensionUnit = () => {
    return userAppState?.units?.dimensionUnit === DimensionUnit.INCH
      ? "in"
      : "cm";
  };

  const getWeightUnit = () => {
    return userAppState?.units?.weightUnit === WeightUnit.POUND ? "lb" : "kg";
  };

  const minDate = dayjs().add(30, "day");
  const maxDate = dayjs().add(1, "year");

  useEffect(() => {
    validateShippingCartItem();
  }, [
    shippingCartItem.itemWeight,
    shippingCartItem.itemLength,
    shippingCartItem.itemWidth,
    shippingCartItem.itemHeight,
    shippingCartItem.storageEndDate,
  ]);

  return (
    <>
      <Grid
        className="ShippingCartItemViewContainer"
        sx={effectiveStyles.ShippingCartItemViewContainer}
      >
        <Divider
          className="ShippingCartItemViewContainerDivider"
          sx={effectiveStyles.ShippingCartItemViewContainerDivider}
        />
        {shippingCartType === ShippingCartType.HOME ? (
          <Grid
            className="ShippingCartItemViewSingleItemRowContainer"
            sx={effectiveStyles.ShippingCartItemViewSingleItemRowContainer}
          >
            <Grid
              sx={
                effectiveStyles.ShippingCartItemViewContentShortTextFieldContainer
              }
            >
              <Grid
                className="ShippingCartItemViewContentShortTextErrorFieldContainer"
                sx={
                  effectiveStyles.ShippingCartItemViewContentShortTextErrorFieldContainer
                }
              >
                <TextField
                  className="ShippingCartItemViewContentShortTextField"
                  sx={effectiveStyles.ShippingCartItemViewContentShortTextField}
                  disabled
                  id="itemLabel"
                  label="Label"
                  value={shippingCartItem.itemLabel || ""}
                />
                {getFieldError("itemLabel", validationErrors) !== null && (
                  <Grid
                    className="ShippingCartItemViewContentShortTextField"
                    sx={
                      effectiveStyles.ShippingCartItemViewContentShortTextField
                    }
                  >
                    <Typography
                      className="ShippingCartItemViewContentShortTextErrorText"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextErrorText
                      }
                      variant={
                        effectiveStyles
                          .ShippingCartItemViewContentShortTextErrorText
                          .muiVariant
                      }
                    >
                      {
                        getFieldError("itemLabel", validationErrors)
                          ?.errorMessage
                      }
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <>
            {multipleItems && (
              <Grid
                className="ShippingCartItemViewRowContainer"
                sx={effectiveStyles.ShippingCartItemViewRowContainer}
              >
                <Grid
                  className="ShippingCartItemViewContentFullWidthTextFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartItemViewContentFullWidthTextFieldContainer
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={shippingCartItem.useTheSameDateAndTime}
                        onChange={handleUseTheSameDateValueChange}
                      />
                    }
                    label="Use the same Date and Time for all items"
                  />
                </Grid>
              </Grid>
            )}
            <Grid
              className="ShippingCartItemViewRowContainer"
              sx={effectiveStyles.ShippingCartItemViewRowContainer}
            >
              <Grid
                sx={
                  effectiveStyles.ShippingCartItemViewContentShortTextFieldContainer
                }
              >
                <Grid
                  className="ShippingCartItemViewContentShortTextErrorFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartItemViewContentShortTextErrorFieldContainer
                  }
                >
                  <TextField
                    className="ShippingCartItemViewContentShortTextField"
                    sx={
                      effectiveStyles.ShippingCartItemViewContentShortTextField
                    }
                    disabled
                    id="itemLabel"
                    label="Label"
                    value={shippingCartItem.itemLabel || ""}
                  />
                  {getFieldError("itemLabel", validationErrors) !== null && (
                    <Grid
                      className="ShippingCartItemViewContentShortTextField"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextField
                      }
                    >
                      <Typography
                        className="ShippingCartItemViewContentShortTextErrorText"
                        sx={
                          effectiveStyles.ShippingCartItemViewContentShortTextErrorText
                        }
                        variant={
                          effectiveStyles
                            .ShippingCartItemViewContentShortTextErrorText
                            .muiVariant
                        }
                      >
                        {
                          getFieldError("itemLabel", validationErrors)
                            ?.errorMessage
                        }
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid
                sx={
                  effectiveStyles.ShippingCartItemViewContentShortTextFieldContainer
                }
              >
                <Grid
                  className="ShippingCartItemViewContentShortTextErrorFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartItemViewContentShortTextErrorFieldContainer
                  }
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={getMuiLocalizationProviderAdapterName()}
                  >
                    <DatePicker
                      className="ShippingCartItemViewContentShortTextField"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextField
                      }
                      label="Store Until"
                      value={dayjs(
                        shippingCartItem.storageEndDate || new Date()
                      )}
                      onChange={onDateChange}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </LocalizationProvider>
                  {getFieldError("storageEndDate", validationErrors) !==
                    null && (
                    <Grid
                      className="ShippingCartItemViewContentShortTextField"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextField
                      }
                    >
                      <Typography
                        className="ShippingCartItemViewContentShortTextErrorText"
                        sx={
                          effectiveStyles.ShippingCartItemViewContentShortTextErrorText
                        }
                        variant={
                          effectiveStyles
                            .ShippingCartItemViewContentShortTextErrorText
                            .muiVariant
                        }
                      >
                        {
                          getFieldError("storageEndDate", validationErrors)
                            ?.errorMessage
                        }
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              className="ShippingCartItemViewRowContainer"
              sx={effectiveStyles.ShippingCartItemViewRowContainer}
            >
              <Grid
                sx={
                  effectiveStyles.ShippingCartItemViewContentShortTextFieldContainer
                }
              >
                <Grid
                  className="ShippingCartItemViewContentShortTextErrorFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartItemViewContentShortTextErrorFieldContainer
                  }
                >
                  <TextField
                    className="ShippingCartItemViewContentShortTextField"
                    sx={
                      effectiveStyles.ShippingCartItemViewContentShortTextField
                    }
                    id="itemLength"
                    label="Length"
                    value={shippingCartItem.itemLength || ""}
                    onChange={onLengthChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {getDimensionUnit()}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {getFieldError("itemLength", validationErrors) !== null && (
                    <Grid
                      className="ShippingCartItemViewContentShortTextField"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextField
                      }
                    >
                      <Typography
                        className="ShippingCartItemViewContentShortTextErrorText"
                        sx={
                          effectiveStyles.ShippingCartItemViewContentShortTextErrorText
                        }
                        variant={
                          effectiveStyles
                            .ShippingCartItemViewContentShortTextErrorText
                            .muiVariant
                        }
                      >
                        {
                          getFieldError("itemLength", validationErrors)
                            ?.errorMessage
                        }
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid
                sx={
                  effectiveStyles.ShippingCartItemViewContentShortTextFieldContainer
                }
              >
                <Grid
                  className="ShippingCartItemViewContentShortTextErrorFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartItemViewContentShortTextErrorFieldContainer
                  }
                >
                  <TextField
                    className="ShippingCartItemViewContentShortTextField"
                    sx={
                      effectiveStyles.ShippingCartItemViewContentShortTextField
                    }
                    id="itemWidth"
                    label="Width"
                    value={shippingCartItem.itemWidth || ""}
                    onChange={onWidthChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {getDimensionUnit()}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {getFieldError("itemWidth", validationErrors) !== null && (
                    <Grid
                      className="ShippingCartItemViewContentShortTextField"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextField
                      }
                    >
                      <Typography
                        className="ShippingCartItemViewContentShortTextErrorText"
                        sx={
                          effectiveStyles.ShippingCartItemViewContentShortTextErrorText
                        }
                        variant={
                          effectiveStyles
                            .ShippingCartItemViewContentShortTextErrorText
                            .muiVariant
                        }
                      >
                        {
                          getFieldError("itemWidth", validationErrors)
                            ?.errorMessage
                        }
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              className="ShippingCartItemViewRowContainer"
              sx={effectiveStyles.ShippingCartItemViewRowContainer}
            >
              <Grid
                sx={
                  effectiveStyles.ShippingCartItemViewContentShortTextFieldContainer
                }
              >
                <Grid
                  className="ShippingCartItemViewContentShortTextErrorFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartItemViewContentShortTextErrorFieldContainer
                  }
                >
                  <TextField
                    className="ShippingCartItemViewContentShortTextField"
                    sx={
                      effectiveStyles.ShippingCartItemViewContentShortTextField
                    }
                    id="itemHeight"
                    label="Height"
                    value={shippingCartItem.itemHeight || ""}
                    onChange={onHeightChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {getDimensionUnit()}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {getFieldError("itemHeight", validationErrors) !== null && (
                    <Grid
                      className="ShippingCartItemViewContentShortTextField"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextField
                      }
                    >
                      <Typography
                        className="ShippingCartItemViewContentShortTextErrorText"
                        sx={
                          effectiveStyles.ShippingCartItemViewContentShortTextErrorText
                        }
                        variant={
                          effectiveStyles
                            .ShippingCartItemViewContentShortTextErrorText
                            .muiVariant
                        }
                      >
                        {
                          getFieldError("itemHeight", validationErrors)
                            ?.errorMessage
                        }
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid
                sx={
                  effectiveStyles.ShippingCartItemViewContentShortTextFieldContainer
                }
              >
                <Grid
                  className="ShippingCartItemViewContentShortTextErrorFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartItemViewContentShortTextErrorFieldContainer
                  }
                >
                  <TextField
                    className="ShippingCartItemViewContentShortTextField"
                    sx={
                      effectiveStyles.ShippingCartItemViewContentShortTextField
                    }
                    id="itemWeight"
                    label="Weight"
                    value={shippingCartItem.itemWeight || ""}
                    onChange={onWeightChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {getWeightUnit()}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {getFieldError("itemWeight", validationErrors) !== null && (
                    <Grid
                      className="ShippingCartItemViewContentShortTextField"
                      sx={
                        effectiveStyles.ShippingCartItemViewContentShortTextField
                      }
                    >
                      <Typography
                        className="ShippingCartItemViewContentShortTextErrorText"
                        sx={
                          effectiveStyles.ShippingCartItemViewContentShortTextErrorText
                        }
                        variant={
                          effectiveStyles
                            .ShippingCartItemViewContentShortTextErrorText
                            .muiVariant
                        }
                      >
                        {
                          getFieldError("itemWeight", validationErrors)
                            ?.errorMessage
                        }
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        <Grid
          className="ShippingCartItemViewContentActionRow"
          sx={effectiveStyles.ShippingCartItemViewContentActionRow}
        >
          <Button
            className="ShippingCartItemViewContentActionRemoveButton"
            sx={effectiveStyles.ShippingCartItemViewContentActionRemoveButton}
            variant={
              effectiveStyles.ShippingCartItemViewContentActionRemoveButton
                .muiVariant
            }
            color={
              effectiveStyles.ShippingCartItemViewContentActionRemoveButton
                .muiColor
            }
            onClick={onRemoveItem}
          >
            Remove from Cart
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
